/**
 * ODIN icon set — lightweight inline stroke icons (no external dependency).
 * Sized by the parent via CSS (width/height on the svg). 1.75 stroke, round.
 */
import type { SVGProps } from "react";

type P = SVGProps<SVGSVGElement>;
const base = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

export const Icon = {
  overview: (p: P) => (
    <svg {...base} {...p}>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  ),
  ai: (p: P) => (
    <svg {...base} {...p}>
      <path d="M12 3l1.8 4.9L18.7 9l-4.9 1.8L12 15.7l-1.8-4.9L5.3 9l4.9-1.1L12 3Z" />
      <path d="M18 15l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7.7-1.9Z" />
    </svg>
  ),
  server: (p: P) => (
    <svg {...base} {...p}>
      <rect x="3" y="4" width="18" height="7" rx="2" />
      <rect x="3" y="13" width="18" height="7" rx="2" />
      <path d="M7 7.5h.01M7 16.5h.01" />
    </svg>
  ),
  members: (p: P) => (
    <svg {...base} {...p}>
      <path d="M16 20v-1.5a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4V20" />
      <circle cx="9.5" cy="7" r="3.2" />
      <path d="M21 20v-1.5a4 4 0 0 0-3-3.8M16.5 4.2a3.2 3.2 0 0 1 0 6.2" />
    </svg>
  ),
  roles: (p: P) => (
    <svg {...base} {...p}>
      <path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6l7-3Z" />
      <path d="M9.5 12l1.8 1.8L15 10" />
    </svg>
  ),
  moderation: (p: P) => (
    <svg {...base} {...p}>
      <path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6l7-3Z" />
      <path d="M12 8v4M12 15.5h.01" />
    </svg>
  ),
  tickets: (p: P) => (
    <svg {...base} {...p}>
      <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2 2 2 0 0 0 0 4 2 2 0 0 1-2 2H5a2 2 0 0 1-2-2 2 2 0 0 0 0-4Z" />
      <path d="M14 6v12" strokeDasharray="2 2" />
    </svg>
  ),
  automations: (p: P) => (
    <svg {...base} {...p}>
      <path d="M13 3 4 14h7l-1 7 9-11h-7l1-7Z" />
    </svg>
  ),
  content: (p: P) => (
    <svg {...base} {...p}>
      <path d="M5 5a14 14 0 0 1 14 14M5 11a8 8 0 0 1 8 8" />
      <circle cx="6" cy="18" r="1.6" />
    </svg>
  ),
  analytics: (p: P) => (
    <svg {...base} {...p}>
      <path d="M4 20V4M4 20h16" />
      <path d="M8 16v-3M12 16V9M16 16v-6" />
    </svg>
  ),
  logs: (p: P) => (
    <svg {...base} {...p}>
      <path d="M8 6h11M8 12h11M8 18h11" />
      <path d="M4 6h.01M4 12h.01M4 18h.01" />
    </svg>
  ),
  integrations: (p: P) => (
    <svg {...base} {...p}>
      <path d="M14 7l3-3 3 3-3 3M10 17l-3 3-3-3 3-3" />
      <path d="M8.5 15.5 15.5 8.5M9 5h2a2 2 0 0 1 2 2v2M15 19h-2a2 2 0 0 1-2-2v-2" />
    </svg>
  ),
  botConfig: (p: P) => (
    <svg {...base} {...p}>
      <rect x="4" y="8" width="16" height="11" rx="2.5" />
      <path d="M12 4v4M8.5 13h.01M15.5 13h.01M9 17h6" />
    </svg>
  ),
  billing: (p: P) => (
    <svg {...base} {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M3 10h18" />
    </svg>
  ),
  settings: (p: P) => (
    <svg {...base} {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </svg>
  ),
  // metrics
  heart: (p: P) => (
    <svg {...base} {...p}>
      <path d="M20.8 5.6a5 5 0 0 0-7.1 0L12 7.3l-1.7-1.7a5 5 0 1 0-7.1 7.1L12 21l8.8-8.3a5 5 0 0 0 0-7.1Z" />
    </svg>
  ),
  userPlus: (p: P) => (
    <svg {...base} {...p}>
      <path d="M15 20v-1.5a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4V20" />
      <circle cx="9" cy="7" r="3.2" />
      <path d="M19 8v6M22 11h-6" />
    </svg>
  ),
  calendar: (p: P) => (
    <svg {...base} {...p}>
      <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
      <path d="M16 2.5v4M8 2.5v4M3 9.5h18" />
    </svg>
  ),
  megaphone: (p: P) => (
    <svg {...base} {...p}>
      <path d="M3 11 18 5v14L3 13v-2ZM3 11v3M18 8a3 3 0 0 1 0 6" />
    </svg>
  ),
  poll: (p: P) => (
    <svg {...base} {...p}>
      <path d="M7 20V10M12 20V4M17 20v-6" />
    </svg>
  ),
  // topbar / misc
  chevronDown: (p: P) => (
    <svg {...base} {...p}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  ),
  search: (p: P) => (
    <svg {...base} {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.2-3.2" />
    </svg>
  ),
  bell: (p: P) => (
    <svg {...base} {...p}>
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9Z" />
      <path d="M10.3 21a2 2 0 0 0 3.4 0" />
    </svg>
  ),
  help: (p: P) => (
    <svg {...base} {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 0 1 4.5 1.5c0 1.7-2.5 2-2.5 3.5M12 17.5h.01" />
    </svg>
  ),
  check: (p: P) => (
    <svg {...base} {...p} strokeWidth={2.2}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  ),
  arrowUp: (p: P) => (
    <svg {...base} {...p} strokeWidth={2.2}>
      <path d="M12 19V5M6 11l6-6 6 6" />
    </svg>
  ),
  external: (p: P) => (
    <svg {...base} {...p}>
      <path d="M14 4h6v6M20 4l-9 9M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" />
    </svg>
  ),
};

export type IconName = keyof typeof Icon;
